/**
 * @copyright
 * Copyright (c) 2022 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import React, { ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LinkProps, useHref, useLinkClickHandler, useLocation } from "react-router-dom";

import { getRouteConfig } from "../../config/route.config";
import { selectUnsavedChangesData } from "~/features/unsavedChanges/selectors";
import { UnsavedChangesActions } from "~/features/unsavedChanges/slice";
import { AppDispatch } from "~/state/types";

interface Props extends LinkProps {
  navigateEnabled?: boolean;
  className?: string;
  children?: ReactNode;
}

const CustomLink = React.forwardRef<HTMLAnchorElement, Props>(function LinkWithRef(
  { onClick, reloadDocument, replace = false, state, target, to, navigateEnabled = true, className, children, ...rest },
  ref,
) {
  const href = useHref(to);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const internalOnClick = useLinkClickHandler(to, { replace, state, target });

  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const unsavedChangesData = useSelector(selectUnsavedChangesData);
  const isDirty = unsavedChangesData.isDirty;

  const routeConfig = getRouteConfig(location.pathname);
  const { warnUnsavedChanges } = routeConfig;

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (warnUnsavedChanges && isDirty) {
      event.preventDefault();
      dispatch(UnsavedChangesActions.setShowModal(to as string));
    } else if (onClick && navigateEnabled) {
      onClick(event);
    }
    if (!navigateEnabled) {
      event.preventDefault();
      return;
    }
    if (!event.defaultPrevented && !reloadDocument) {
      internalOnClick(event);
    }
  };

  return (
    <a
      {...rest}
      href={navigateEnabled ? href : undefined}
      onClick={handleClick}
      ref={ref}
      target={target}
      className={className}
    >
      {children}
    </a>
  );
});

export default CustomLink;
